import { Link } from 'react-router-dom';
import type { Student } from '@/types';
import type { SubjectTrend } from '@/lib/scores';
import { daysBetween, formatElapsedDays, formatNumber, tuitionStatus } from '@/lib/datetime';
import { dayTimeLabels } from '@/lib/schedule';
import StudentScoreTrend from './StudentScoreTrend';

interface StudentCardItemProps {
  student: Student;
  trends?: SubjectTrend[];
}

export default function StudentCardItem({ student, trends = [] }: StudentCardItemProps) {
  const initial = (student.name || '?').trim().charAt(0).toUpperCase();
  const elapsed = formatElapsedDays(daysBetween(student.first_lesson_date));
  const days = student.class_days ?? [];
  const timeLabels = dayTimeLabels(student, days);
  const { status } = tuitionStatus(student.tuition_due_day, student.tuition_last_paid_at);

  return (
    <Link
      to={`/students/${student.id}`}
      className="group flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-lg font-bold text-secondary-900">
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate text-base font-bold text-foreground-950">{student.name}</p>
            {student.target_school && (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-xs font-semibold text-accent-800">
                <i className="ri-flag-2-line"></i>
                {student.target_school}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-foreground-500">
            {student.school || '학교 미입력'} · {student.grade || '학년 미입력'}
          </p>
        </div>
        <i className="ri-arrow-right-s-line text-foreground-400 transition group-hover:text-primary-600"></i>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {days.length === 0 ? (
          <span className="rounded-md bg-background-100 px-2 py-1 text-xs text-foreground-500">
            수업 요일 미입력
          </span>
        ) : (
          days.map((d) => (
            <span
              key={d}
              className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-xs font-semibold text-primary-700"
            >
              {d}
            </span>
          ))
        )}
        {timeLabels.length > 0 && (
          <span className="flex items-center gap-1 rounded-md bg-background-100 px-2 py-1 text-xs font-medium text-foreground-700">
            <i className="ri-time-line"></i>
            {timeLabels.join(' · ')}
          </span>
        )}
      </div>

      <StudentScoreTrend trends={trends} />

      {(student.parent_contact || student.lesson_location) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 border-t border-background-200 pt-4 text-xs text-foreground-600">
          {student.parent_contact && (
            <span className="flex items-center gap-1.5">
              <i className="ri-parent-line text-secondary-600"></i>
              <span className="text-foreground-500">학부모</span>
              <span className="font-medium text-foreground-800">{student.parent_contact}</span>
            </span>
          )}
          {student.lesson_location && (
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-2-line text-primary-600"></i>
              <span className="text-foreground-500">교습장소</span>
              <span className="font-medium text-foreground-800">{student.lesson_location}</span>
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 border-t border-background-200 pt-4">
        <div>
          <p className="text-xs text-foreground-500">함께한 기간</p>
          <p className="mt-0.5 text-sm font-bold text-primary-700">{elapsed}</p>
        </div>
        <div>
          <p className="flex items-center gap-1.5 text-xs text-foreground-500">
            교습비
            {status === 'overdue' && (
              <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold text-accent-800">
                <i className="ri-error-warning-line"></i>
                미납
              </span>
            )}
          </p>
          <p className="mt-0.5 text-sm font-bold text-foreground-900">
            {student.tuition_fee ? `${formatNumber(student.tuition_fee)}원` : '미입력'}
          </p>
        </div>
      </div>
    </Link>
  );
}